import { useEffect, useMemo, useState } from "react";
import { MdDownload } from "react-icons/md";
import { useAuth } from "../context/AuthContext";
import { useAdminUsers } from "../hooks/useAdminUsers";
import { getRegisterOptions } from "../services/authService";
import {
  exportUsers,
  createAdminUser,
  disableUser,
  enableUser,
  updateUser,
} from "../services/usersService";
import { AdminUsersList } from "../components/admin/AdminUsersList";
import {
  CreateAdminModal,
  UserEditModal,
  UserStatusModal,
} from "../components/admin/AdminUsersModals";
import { BrandButton } from "../components/ui/BrandButton";
import { brandInputClass } from "../components/form/fieldStyles";
import { useAppLoading } from "../context/AppLoadingContext";

const PAGE_SIZE = 20;
const SEARCH_DEBOUNCE_MS = 300;

export function AdminUsersPage() {
  const { trackAsync } = useAppLoading();
  const { user } = useAuth();
  const isSuperadmin = user?.role?.name === "superadmin";
  const canExportUsers = user?.role?.name === "admin" || isSuperadmin;
  const [page, setPage] = useState(1);
  const [searchInput, setSearchInput] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedUser, setSelectedUser] = useState(null);
  const [selectedStatusUser, setSelectedStatusUser] = useState(null);
  const [selectedStatusAction, setSelectedStatusAction] = useState("disable");
  const [isSaving, setIsSaving] = useState(false);
  const [notice, setNotice] = useState("");
  const [submitError, setSubmitError] = useState("");
  const [isExporting, setIsExporting] = useState(false);
  const [isLoadingOptions, setIsLoadingOptions] = useState(true);
  const [options, setOptions] = useState({
    institutions: [],
    tnuaOptions: [],
  });
  const [isCreateAdminOpen, setIsCreateAdminOpen] = useState(false);

  const { users, pagination, isLoading, error, refresh } = useAdminUsers(page, PAGE_SIZE, searchQuery);

  useEffect(() => {
    const timeoutId = window.setTimeout(() => {
      setSearchQuery(searchInput.trim());
      setPage(1);
    }, SEARCH_DEBOUNCE_MS);

    return () => window.clearTimeout(timeoutId);
  }, [searchInput]);

  useEffect(() => {
    let isMounted = true;

    const loadOptions = async () => {
      try {
        const response = await getRegisterOptions();

        if (!isMounted) {
          return;
        }

        setOptions({
          institutions: response.institutions ?? [],
          tnuaOptions: response.tnuaOptions ?? [],
        });
      } catch {
        if (!isMounted) {
          return;
        }
      } finally {
        if (isMounted) {
          setIsLoadingOptions(false);
        }
      }
    };

    void trackAsync(loadOptions);

    return () => {
      isMounted = false;
    };
  }, [trackAsync]);

  const filterSummary = useMemo(() => {
    const trimmed = searchQuery.trim();
    if (!trimmed) {
      return "Mostrando todos los usuarios";
    }

    return `Buscando “${trimmed}”`;
  }, [searchQuery]);

  const handleCloseModals = () => {
    setSelectedUser(null);
    setSelectedStatusUser(null);
    setIsCreateAdminOpen(false);
    setSubmitError("");
    setNotice("");
  };

  const handleEditSubmit = async (payload) => {
    if (!selectedUser) {
      return;
    }

    setIsSaving(true);
    setSubmitError("");

    try {
      await updateUser(selectedUser.id, payload);
      setSelectedUser(null);
      setNotice("Usuario actualizado correctamente.");
      refresh();
    } catch (requestError) {
      setSubmitError(requestError.message || "No pudimos actualizar el usuario.");
    } finally {
      setIsSaving(false);
    }
  };

  const handleToggleStatus = async () => {
    if (!selectedStatusUser) {
      return;
    }

    setIsSaving(true);
    setSubmitError("");

    try {
      if (selectedStatusAction === "disable") {
        await disableUser(selectedStatusUser.id);
        setNotice("Usuario deshabilitado.");
      } else {
        await enableUser(selectedStatusUser.id);
        setNotice("Usuario habilitado.");
      }

      setSelectedStatusUser(null);
      refresh();
    } catch (requestError) {
      setSubmitError(requestError.message || "No pudimos cambiar el estado del usuario.");
    } finally {
      setIsSaving(false);
    }
  };

  const handleCreateAdminSubmit = async (payload) => {
    setIsSaving(true);
    setSubmitError("");

    try {
      await createAdminUser(payload);
      setIsCreateAdminOpen(false);
      setNotice("Admin creado correctamente.");
      refresh();
    } catch (requestError) {
      setSubmitError(requestError.message || "No pudimos crear el admin.");
    } finally {
      setIsSaving(false);
    }
  };

  const handleExportUsers = async () => {
    setIsExporting(true);
    setSubmitError("");

    try {
      const blob = await exportUsers({ q: searchQuery });
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement("a");

      link.href = downloadUrl;
      link.download = "usuarios.xlsx";
      link.rel = "noopener";

      document.body.appendChild(link);
      link.click();
      link.remove();
      window.setTimeout(() => window.URL.revokeObjectURL(downloadUrl), 0);
    } catch (requestError) {
      setSubmitError(requestError.message || "No pudimos exportar los usuarios.");
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <div className="grid gap-5">
      <section className="flex flex-col gap-4 px-1 py-1 sm:px-2 sm:py-2 lg:flex-row lg:items-center lg:justify-between">
        <h2 className="font-display text-3xl text-brand-primary sm:text-4xl">
          Gestión de usuarios
        </h2>
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center lg:justify-end">
          {canExportUsers ? (
            <BrandButton
              type="button"
              onClick={handleExportUsers}
              variant="outline"
              radius="2xl"
              size="lg"
              disabled={isExporting}
            >
              <MdDownload className="text-base" />
              {isExporting ? "Exportando..." : "Exportar Excel"}
            </BrandButton>
          ) : null}
          {isSuperadmin ? (
            <BrandButton
              type="button"
              onClick={() => setIsCreateAdminOpen(true)}
              variant="secondary"
              radius="2xl"
              size="lg"
            >
              Crear admin
            </BrandButton>
          ) : null}
        </div>
      </section>

      <section className="grid gap-4 rounded-[2rem] border border-brand-secondary/15 bg-white p-4 sm:p-5">
        <div className="grid gap-3 lg:grid-cols-[minmax(0,1fr)_auto] lg:items-end">
          <label className="grid gap-2 lg:max-w-xl lg:flex-1">
            <span className="text-sm font-semibold tracking-wide text-brand-primary">
              Buscar usuarios
            </span>
            <input
              value={searchInput}
              onChange={(event) => setSearchInput(event.target.value)}
              placeholder="Nombre, apellido, usuario o email"
              className={brandInputClass}
            />
          </label>
          <div className="grid gap-1">
            <p className="text-xs font-medium uppercase tracking-[0.18em] text-brand-secondary">
              {isLoadingOptions ? "Cargando filtros..." : filterSummary}
            </p>
          </div>
        </div>
      </section>

      {notice ? (
        <div className="rounded-[2rem] border border-emerald-200 bg-emerald-50 px-6 py-4 text-sm text-emerald-700 ">
          {notice}
        </div>
      ) : null}

      {submitError ? (
        <div className="rounded-[2rem] border border-rose-200 bg-rose-50 px-6 py-4 text-sm text-rose-700">
          {submitError}
        </div>
      ) : null}

      {isLoading ? (
        <div className="rounded-[2rem] border border-brand-secondary/15 bg-white px-6 py-10 text-center text-sm text-slate-500 ">
          Cargando usuarios...
        </div>
      ) : error ? (
        <div className="rounded-[2rem] border border-rose-200 bg-rose-50 px-6 py-10 text-center text-sm text-rose-700 ">
          {error}
        </div>
      ) : (
        <AdminUsersList
          users={users}
          pagination={pagination}
          currentUserId={user?.id}
          onEditClick={setSelectedUser}
          onToggleStatusClick={(currentUser) => {
            setSelectedStatusUser(currentUser);
            setSelectedStatusAction(currentUser.disabledAt ? "enable" : "disable");
          }}
          onPrevious={() => setPage((current) => Math.max(1, current - 1))}
          onNext={() =>
            setPage((current) =>
              pagination.totalPages > 0
                ? Math.min(pagination.totalPages, current + 1)
                : current + 1,
            )
          }
        />
      )}

      {selectedUser ? (
        <UserEditModal
          user={selectedUser}
          canEditRole={isSuperadmin}
          institutions={options.institutions}
          tnuaOptions={options.tnuaOptions}
          isSaving={isSaving}
          onClose={handleCloseModals}
          onSubmit={handleEditSubmit}
        />
      ) : null}

      {selectedStatusUser ? (
        <UserStatusModal
          user={selectedStatusUser}
          action={selectedStatusAction}
          isSaving={isSaving}
          onClose={handleCloseModals}
          onConfirm={handleToggleStatus}
        />
      ) : null}

      {isCreateAdminOpen ? (
        <CreateAdminModal
          isSaving={isSaving}
          onClose={handleCloseModals}
          onSubmit={handleCreateAdminSubmit}
        />
      ) : null}
    </div>
  );
}
